import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { countryCodes, flagEmoji } from '../utils/countryCodes';
import {
  Shield,
  Smartphone,
  Mail,
  MessageSquare,
  ArrowLeft,
  ArrowRight,
  Loader,
  Check,
  Copy,
  Download,
  AlertTriangle,
} from 'lucide-react';
import { mfaApi } from '../services/authApi';
import { Logo } from '../components/Logo';
import type { MfaMethod, MfaTotpSetupResponse, MfaBackupCodesResponse } from '../types';

type Step = 'choose' | 'configure' | 'backup' | 'done';

const METHOD_OPTIONS: { value: MfaMethod; icon: React.ElementType; label: string; desc: string }[] = [
  { value: 'TOTP', icon: Smartphone, label: 'Authenticator App', desc: 'Google Authenticator, Authy, or similar' },
  { value: 'EMAIL', icon: Mail, label: 'Email OTP', desc: 'Receive a code via your registered email' },
  { value: 'SMS', icon: MessageSquare, label: 'SMS OTP', desc: 'Receive a code via text message' },
];

interface MfaSetupPageProps {
  /** When true, renders as a full-screen onboarding page with a "Skip for now" option. */
  onboarding?: boolean;
}

export const MfaSetupPage: React.FC<MfaSetupPageProps> = ({ onboarding = false }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('choose');
  const [method, setMethod] = useState<MfaMethod>('TOTP');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // TOTP state
  const [totpSetup, setTotpSetup] = useState<MfaTotpSetupResponse | null>(null);
  const [totpCode, setTotpCode] = useState('');

  // SMS state
  const [diallingCode, setDiallingCode] = useState('+44');
  const [localNumber, setLocalNumber] = useState('');
  const phoneNumber = `${diallingCode}${localNumber.replace(/^0+/, '')}`;
  const [smsCode, setSmsCode] = useState('');
  const [smsSent, setSmsSent] = useState(false);

  // Backup codes
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [codesAcknowledged, setCodesAcknowledged] = useState(false);
  const [codesCopied, setCodesCopied] = useState(false);

  const exitDestination = onboarding ? '/home' : '/settings';
  const exitLabel = onboarding ? 'Skip for now' : 'Back to Settings';

  const handleChoose = () => {
    setError('');
    setStep('configure');
    if (method === 'TOTP') {
      initTotp();
    }
  };

  const initTotp = async () => {
    setLoading(true);
    try {
      const setup = await mfaApi.setupTotp();
      setTotpSetup(setup);
    } catch (err: any) {
      setError(err?.message || 'Failed to set up authenticator');
    } finally {
      setLoading(false);
    }
  };

  const confirmTotp = async () => {
    setError('');
    setLoading(true);
    try {
      await mfaApi.confirmTotp(totpCode);
      await enableAndGetBackupCodes();
    } catch (err: any) {
      setError(err?.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const enableEmail = async () => {
    setError('');
    setLoading(true);
    try {
      await mfaApi.enableEmail();
      await enableAndGetBackupCodes();
    } catch (err: any) {
      setError(err?.message || 'Failed to enable email OTP');
    } finally {
      setLoading(false);
    }
  };

  const startSmsSetup = async () => {
    setError('');
    setLoading(true);
    try {
      await mfaApi.setupSms(phoneNumber);
      setSmsSent(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to send SMS');
    } finally {
      setLoading(false);
    }
  };

  const confirmSms = async () => {
    setError('');
    setLoading(true);
    try {
      await mfaApi.confirmSms(smsCode);
      await enableAndGetBackupCodes();
    } catch (err: any) {
      setError(err?.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const enableAndGetBackupCodes = async () => {
    const result: MfaBackupCodesResponse = await mfaApi.enable(method);
    setBackupCodes(result.backupCodes);
    setStep('backup');
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCodesCopied(true);
    setTimeout(() => setCodesCopied(false), 2000);
  };

  const downloadBackupCodes = () => {
    const content = `ClinLexis MFA Backup Codes\n${'='.repeat(40)}\n\n${backupCodes.join('\n')}\n\nKeep these codes somewhere safe.\nEach code can only be used once.\n`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'clinlexis-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const finishSetup = () => {
    navigate(exitDestination, { replace: true });
  };

  // ── Shared card content (identical in both modes) ──────────────────

  const cardContent = (
    <>
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
          <Shield className="h-6 w-6 text-primary-600 dark:text-primary-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-medical-900 dark:text-medical-100">
            {onboarding ? 'Secure Your Account' : 'Set Up Two-Factor Authentication'}
          </h1>
          <p className="text-sm text-medical-600 dark:text-medical-400">
            {onboarding
              ? 'We strongly recommend adding two-factor authentication'
              : 'Add an extra layer of security to your account'}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="flex items-center space-x-2 mb-8">
        {(['choose', 'configure', 'backup'] as Step[]).map((s, i) => (
          <React.Fragment key={s}>
            <div className={`h-2 flex-1 rounded-full ${
              step === s ? 'bg-primary-500' :
              (['choose', 'configure', 'backup'].indexOf(step) > i ? 'bg-primary-300' : 'bg-medical-200 dark:bg-medical-600')
            }`} />
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3 mb-4">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Step 1: Choose method */}
      {step === 'choose' && (
        <div className="space-y-4">
          <h2 className="text-lg font-medium text-medical-900 dark:text-medical-100">Choose your preferred method</h2>
          <div className="space-y-3">
            {METHOD_OPTIONS.map(({ value, icon: Icon, label, desc }) => (
              <button
                key={value}
                onClick={() => setMethod(value)}
                className={`w-full flex items-center space-x-4 p-4 rounded-lg border-2 transition-all text-left ${
                  method === value
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-medical-200 dark:border-medical-600 hover:border-medical-300 dark:hover:border-medical-500'
                }`}
              >
                <Icon className={`h-6 w-6 ${method === value ? 'text-primary-600' : 'text-medical-400'}`} />
                <div>
                  <p className={`font-medium ${method === value ? 'text-primary-700 dark:text-primary-300' : 'text-medical-900 dark:text-medical-100'}`}>{label}</p>
                  <p className="text-sm text-medical-500 dark:text-medical-400">{desc}</p>
                </div>
                {method === value && <Check className="h-5 w-5 text-primary-600 ml-auto" />}
              </button>
            ))}
          </div>
          <div className={`flex ${onboarding ? 'justify-between' : 'justify-end'} pt-4`}>
            {onboarding && (
              <button
                onClick={() => navigate('/home', { replace: true })}
                className="text-sm text-medical-500 hover:text-medical-700 transition-colors"
              >
                Skip for now
              </button>
            )}
            <button onClick={handleChoose} className="btn-primary flex items-center space-x-2">
              <span>Continue</span>
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Configure — TOTP */}
      {step === 'configure' && method === 'TOTP' && (
        <div className="space-y-4">
          <h2 className="text-lg font-medium text-medical-900 dark:text-medical-100">Set up your authenticator app</h2>
          {loading && !totpSetup ? (
            <div className="flex justify-center py-8">
              <Loader className="h-8 w-8 animate-spin text-primary-500" />
            </div>
          ) : totpSetup ? (
            <>
              <div className="bg-medical-50 dark:bg-medical-700 rounded-lg p-4">
                <p className="text-sm text-medical-700 dark:text-medical-300 mb-3">
                  Scan this QR code with your authenticator app, or manually enter the secret key below.
                </p>
                <div className="flex justify-center mb-3">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(totpSetup.qrCodeUri)}`}
                    alt="QR Code for authenticator setup"
                    className="w-48 h-48 rounded-lg bg-white p-2"
                  />
                </div>
                <div className="text-center">
                  <p className="text-xs text-medical-500 dark:text-medical-400 mb-1">Secret key (manual entry):</p>
                  <code className="text-sm font-mono bg-white dark:bg-medical-800 px-3 py-1 rounded border border-medical-200 dark:border-medical-600 select-all">
                    {totpSetup.secret}
                  </code>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-medical-700 dark:text-medical-300 mb-2">
                  Enter the 6-digit code from your app
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  className="input-field text-center text-2xl tracking-[0.3em] font-mono"
                  placeholder="000000"
                  autoFocus
                />
              </div>
              <div className="flex justify-between pt-4">
                <button onClick={() => setStep('choose')} className="btn-secondary">Back</button>
                <button
                  onClick={confirmTotp}
                  disabled={loading || totpCode.length !== 6}
                  className="btn-primary flex items-center space-x-2 disabled:opacity-50"
                >
                  {loading ? <Loader className="h-4 w-4 animate-spin" /> : null}
                  <span>Verify & Enable</span>
                </button>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* Step 2: Configure — Email */}
      {step === 'configure' && method === 'EMAIL' && (
        <div className="space-y-4">
          <h2 className="text-lg font-medium text-medical-900 dark:text-medical-100">Enable email verification</h2>
          <p className="text-sm text-medical-600 dark:text-medical-400">
            A one-time code will be sent to your registered email each time you sign in.
          </p>
          <div className="flex justify-between pt-4">
            <button onClick={() => setStep('choose')} className="btn-secondary">Back</button>
            <button
              onClick={enableEmail}
              disabled={loading}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50"
            >
              {loading ? <Loader className="h-4 w-4 animate-spin" /> : null}
              <span>Enable Email OTP</span>
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Configure — SMS */}
      {step === 'configure' && method === 'SMS' && (
        <div className="space-y-4">
          <h2 className="text-lg font-medium text-medical-900 dark:text-medical-100">Set up SMS verification</h2>
          {!smsSent ? (
            <>
              <div>
                <label className="block text-sm font-medium text-medical-700 dark:text-medical-300 mb-2">
                  Phone number
                </label>
                <div className="flex gap-2">
                  <select
                    value={diallingCode}
                    onChange={(e) => setDiallingCode(e.target.value)}
                    className="input-field w-44 flex-shrink-0"
                  >
                    {countryCodes.map((c) => (
                      <option key={`${c.country}-${c.code}`} value={c.code}>
                        {flagEmoji(c.country)} {c.country} ({c.code})
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={localNumber}
                    onChange={(e) => setLocalNumber(e.target.value.replace(/[^0-9\s]/g, ''))}
                    className="input-field flex-1"
                    placeholder="7911 123456"
                    autoFocus
                  />
                </div>
                <p className="text-xs text-medical-500 dark:text-medical-400 mt-1">Leading zero is removed automatically</p>
              </div>
              <div className="flex justify-between pt-4">
                <button onClick={() => setStep('choose')} className="btn-secondary">Back</button>
                <button
                  onClick={startSmsSetup}
                  disabled={loading || !localNumber.trim()}
                  className="btn-primary flex items-center space-x-2 disabled:opacity-50"
                >
                  {loading ? <Loader className="h-4 w-4 animate-spin" /> : null}
                  <span>Send Verification Code</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-medical-600 dark:text-medical-400">
                We sent a code to <strong>{phoneNumber}</strong>.
              </p>
              <div>
                <label className="block text-sm font-medium text-medical-700 dark:text-medical-300 mb-2">
                  Enter the 6-digit code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, ''))}
                  className="input-field text-center text-2xl tracking-[0.3em] font-mono"
                  placeholder="000000"
                  autoFocus
                />
              </div>
              <div className="flex justify-between pt-4">
                <button onClick={() => { setSmsSent(false); setSmsCode(''); }} className="btn-secondary">Change Number</button>
                <button
                  onClick={confirmSms}
                  disabled={loading || smsCode.length !== 6}
                  className="btn-primary flex items-center space-x-2 disabled:opacity-50"
                >
                  {loading ? <Loader className="h-4 w-4 animate-spin" /> : null}
                  <span>Verify & Enable</span>
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 3: Backup codes */}
      {step === 'backup' && (
        <div className="space-y-4">
          <div className="flex items-center space-x-2 text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-5 w-5" />
            <h2 className="text-lg font-medium">Save Your Backup Codes</h2>
          </div>
          <p className="text-sm text-medical-600 dark:text-medical-400">
            These one-time-use codes allow you to sign in if you lose access to your primary MFA method.
            Each code can only be used once. Store them securely.
          </p>

          <div className="bg-medical-50 dark:bg-medical-700 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((code, i) => (
                <code key={i} className="text-sm font-mono text-center py-1.5 bg-white dark:bg-medical-800 rounded border border-medical-200 dark:border-medical-600">
                  {code}
                </code>
              ))}
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={copyBackupCodes}
              className="btn-secondary flex items-center space-x-2 flex-1"
            >
              {codesCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              <span>{codesCopied ? 'Copied!' : 'Copy'}</span>
            </button>
            <button
              onClick={downloadBackupCodes}
              className="btn-secondary flex items-center space-x-2 flex-1"
            >
              <Download className="h-4 w-4" />
              <span>Download</span>
            </button>
          </div>

          <label className="flex items-center space-x-2 cursor-pointer pt-2">
            <input
              type="checkbox"
              checked={codesAcknowledged}
              onChange={(e) => setCodesAcknowledged(e.target.checked)}
              className="rounded border-medical-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-medical-700 dark:text-medical-300">
              I have saved my backup codes in a secure location
            </span>
          </label>

          <div className="flex justify-end pt-4">
            <button
              onClick={finishSetup}
              disabled={!codesAcknowledged}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50"
            >
              <span>{onboarding ? 'Continue to Dashboard' : 'Finish Setup'}</span>
              <Check className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );

  // ── Onboarding mode: full-screen standalone layout ─────────────────

  if (onboarding) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-medical-50 via-white to-primary-50 dark:from-medical-900 dark:via-medical-800 dark:to-medical-900 flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <div className="mb-6 flex items-center justify-between">
            <Logo size="md" />
            <button
              onClick={() => navigate('/home', { replace: true })}
              className="text-sm text-medical-500 dark:text-medical-400 hover:text-medical-700 dark:hover:text-medical-200 transition-colors"
            >
              {exitLabel} &rarr;
            </button>
          </div>

          <div className="bg-white dark:bg-medical-800 rounded-2xl shadow-xl dark:shadow-2xl dark:shadow-black/20 border border-medical-200 dark:border-medical-700 p-8">
            {cardContent}
          </div>

          <div className="mt-4 flex items-center justify-center space-x-2 text-xs text-medical-500 dark:text-medical-400">
            <Shield className="h-4 w-4" />
            <span>You can always set this up later in Settings</span>
          </div>
        </div>
      </div>
    );
  }

  // ── Settings mode: in-app layout ───────────────────────────────────

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/settings')}
          className="inline-flex items-center space-x-2 text-medical-600 dark:text-medical-400 hover:text-medical-900 dark:hover:text-medical-200 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>{exitLabel}</span>
        </button>
      </div>

      <div className="card dark:bg-medical-800 dark:border-medical-700">
        {cardContent}
      </div>
    </div>
  );
};
